import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, Btn, Empty } from "@/components/common";
import { RISK_CLASS } from "@/lib/ui";
import { ScrollText, Plus, X, History } from "lucide-react";
import { toast } from "sonner";

const SAMPLES = {
  powershell: "Write-Output (hostname)",
  cmd: "echo %COMPUTERNAME%",
  bash: "hostname",
};

export default function Scripts() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [show, setShow] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", language: "powershell", risk: "LOW", category: "General", content: SAMPLES.powershell });

  const load = () => api.get("/scripts").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/scripts", form); toast.success("Script saved"); setShow(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!rows) return <div className="p-6"><Spinner /></div>;
  return (
    <div className="p-6 fade-up">
      <PageHeader title="Script Library" subtitle={`${rows.length} scripts · versioned & risk-rated`} testid="scripts-page"
        actions={can("scripts:create") && <Btn data-testid="new-script-btn" onClick={() => { setForm({ ...form, content: SAMPLES.powershell }); setShow(true); }}><Plus size={15} /> New Script</Btn>} />

      {rows.length === 0 ? <Empty icon={ScrollText} title="No scripts yet" hint="Create a PowerShell, CMD or Bash script. It becomes runnable on any device." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((s) => (
            <div key={s.id} className="card-surface rounded-lg p-4 cursor-pointer hover:border-cyan-800 transition-colors" data-testid="script-card"
              onClick={() => api.get("/scripts/" + s.id).then((r) => setDetail(r.data))}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-heading font-bold text-slate-100">{s.name}</span>
                <span className={"text-[10px] font-bold uppercase rounded border px-1.5 py-0.5 " + (RISK_CLASS[s.risk] || RISK_CLASS.LOW)}>{s.risk}</span>
              </div>
              <p className="text-[12px] text-slate-500 line-clamp-2 h-8">{s.description || "No description"}</p>
              <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500 font-mono">
                <span className="bg-slate-800 rounded px-1.5 py-0.5">{s.language}</span>
                <span>v{s.version}</span><span>·</span><span>{s.author}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {(show || detail) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => { setShow(false); setDetail(null); }}>
          <div className="card-surface rounded-xl p-5 w-full max-w-2xl mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-slate-100">{detail ? detail.name : "New Script"}</h2>
              <button onClick={() => { setShow(false); setDetail(null); }} className="text-slate-500 hover:text-slate-200"><X size={18} /></button>
            </div>
            {detail ? (
              <div>
                <div className="flex items-center gap-2 mb-2"><span className={"text-[10px] font-bold uppercase rounded border px-1.5 py-0.5 " + (RISK_CLASS[detail.risk] || RISK_CLASS.LOW)}>{detail.risk}</span><span className="text-[12px] text-slate-400 font-mono">{detail.language} · v{detail.version}</span></div>
                <p className="text-[13px] text-slate-400 mb-2">{detail.description}</p>
                <pre className="bg-slate-950 border border-slate-800 rounded-md p-3 text-[12px] text-emerald-300 overflow-x-auto max-h-64 whitespace-pre-wrap">{detail.content}</pre>
                <div className="flex items-center gap-2 mt-3 text-[12px] text-slate-500"><History size={13} /> {detail.versions?.length || 1} revision(s) — immutable history</div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><L>Name</L><input data-testid="script-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" /></div>
                <div className="col-span-2"><L>Description</L><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="inp" /></div>
                <div><L>Language</L><select data-testid="script-language" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value, content: SAMPLES[e.target.value] })} className="inp">{["powershell", "cmd", "bash"].map((l) => <option key={l}>{l}</option>)}</select></div>
                <div><L>Risk</L><select value={form.risk} onChange={(e) => setForm({ ...form, risk: e.target.value })} className="inp">{["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((r) => <option key={r}>{r}</option>)}</select></div>
                <div className="col-span-2"><L>Script</L><textarea data-testid="script-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={7} className="inp font-mono text-[12px]" /></div>
                <div className="col-span-2 flex justify-end gap-2"><Btn variant="ghost" onClick={() => setShow(false)}>Cancel</Btn><Btn data-testid="script-save-btn" onClick={create} disabled={!form.name || !form.content}>Save Script</Btn></div>
              </div>
            )}
          </div>
        </div>
      )}
      <style>{`.inp{width:100%;background:#0b1220;border:1px solid #334155;border-radius:6px;padding:8px 12px;font-size:13px;color:#e2e8f0;outline:none}`}</style>
    </div>
  );
}
function L({ children }) { return <label className="text-[12px] text-slate-400 block mb-1">{children}</label>; }
